import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Container,
  Fade,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import {
  DataGrid,
  GridColumns,
  GridRenderCellParams,
  GridSelectionModel,
  GridValueGetterParams,
} from '@mui/x-data-grid'
import {
  Avatar,
  Icon,
  Modal,
  SnackBarContext,
  IconToggle,
  ConfirmDialog,
} from 'components'
import { DatagridPlaceholder } from 'components/datagridPlaceholder'
import { theme } from 'config'
import { inject, observer } from 'mobx-react'
import { User } from 'models'
import React, { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RoutingStore, AuthStore } from 'stores'
import styled from 'styled-components'
import { Language, UserType } from 'types'
import { UserForm } from 'views'
import { Loading } from 'views/loading'
import { UserImport } from './importUsers'
import { caseInsensitiveComparator } from 'util/util'

export interface IListUsersProps {
  routingStore?: RoutingStore
  authStore?: AuthStore
}

type RowData = {
  id: number
  user: User
  details?: ReactNode
  name: string
  email: string
  license?: string
  courses: any
  progress: number
}

export const ListUsers = inject(
  'routingStore',
  'authStore'
)(
  observer((props: IListUsersProps): JSX.Element => {
    const { t } = useTranslation()
    const { toast } = React.useContext(SnackBarContext)
    const [user, setUser] = React.useState<User>()
    const [users, setUsers] = React.useState<User[]>([])
    const [openModal, setOpenModal] = React.useState<boolean>(false)
    const [showFakeData, setShowFakeData] = React.useState<boolean>(false)
    const [openImportModal, setOpenImportModal] = React.useState<boolean>(false)
    const [isLoading, setIsLoading] = React.useState<boolean>(true)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const [confirmValue, setConfirmValue] = React.useState<string>('')
    const [selectedRowsIds, setSelectedRowsIds] = React.useState<number[]>([])
    const [openConfirmDialog, setOpenConfirmDialog] =
      React.useState<boolean>(false)
      
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    React.useEffect(() => {
      let isMounted = true

      if (
        props.authStore.getCurUser &&
        props.authStore.getCurUser.user_type === UserType.Manager
      ) {
        setShowFakeData(true)
      }

      User.get().then(async (u: User[]) => {
        for (const _u of u) {
          await _u.loadCourses()
        }

        if (isMounted) {
          setUsers(u || [])
          setIsLoading(false)
        }
      })

      return () => {
        isMounted = false
      }
    }, [])

    const renderDetailsButton = (params: GridRenderCellParams<RowData>) => {
      return (
        <IconButton
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget)
            setUser(params.row.user)
          }}
        >
          <MoreVertIcon htmlColor={theme.palette.black.main} />
        </IconButton>
      )
    }

    const renderHeaderButton = () => {
      return (
        <IconToggle
          iconOn={<Icon size='size-12' iconId='lock' />}
          iconOff={<Icon size='size-12' iconId='unlock' />}
          selected={showFakeData}
          onChange={() => setShowFakeData(!showFakeData)}
          tooltipIconOff={t('hide_real_data')}
          tooltipIconOn={t('show_real_data')}
        />
      )
    }

    const columns: GridColumns = [
      {
        flex: 1,
        field: 'name',
        headerName: t('name'),
        headerClassName: 'columnHeader',
        renderCell: (params: GridRenderCellParams<[], RowData>) => (
          <Avatar
            name={showFakeData && t('fake_user_name')}
            fakeEmail={showFakeData}
            user={params.row.user}
          />
        ),
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          params.row.user.getFullName,
        sortComparator: caseInsensitiveComparator,
      },
      {
        flex: 1,
        field: 'courses',
        headerName: t('courses'),
        headerClassName: 'columnHeader',
        headerAlign: 'left',
        align: 'left',
      },
      {
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        field: 'progress',
        headerName: t('progress'),
        headerClassName: 'columnHeader',
        renderCell: (params: GridRenderCellParams<number, RowData>) =>
          params.value === 0 ? (
            <Typography fontSize={theme.typography.fontSize}>
              {t('unstarted')}
            </Typography>
          ) : (
            params.value > 0 && (
              <Typography fontSize={theme.typography.fontSize}>
                {params.row.progress}%
              </Typography>
            )
          ),
      },
      {
        headerClassName: 'columnHeader',
        field: 'action',
        headerName: '',
        renderHeader: renderHeaderButton,
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        editable: false,
        align: 'center',
        renderCell: renderDetailsButton,
      },
    ]

    const rows: { [id: number]: RowData } = React.useMemo(
      () =>
        users.reduce((res: { [id: number]: RowData }, val) => {
          res[val.id] = {
            id: val.id,
            user: val,
            details: [val.getFullName, val.email],
            name: val.getFullName,
            email: val.email,
            courses: val.courses?.length,
            progress: Math.round(val.progress),
          }
          return res
        }, {}),
      [users, showFakeData]
    )

    const email = selectedRowsIds.map((id) => `${rows[id].email};, `)
    const rowsArray = Object.values(rows).sort((row1, row2) =>
      row1.name > row2.name ? 1 : -1
    )

    const PAGE_SIZE = 30
    const hideFooter = rowsArray.length < PAGE_SIZE
    const buttonSize = isMobile
      ? {
          minWidth: 150,
          padding: '0.5rem 1.5rem',
        }
      : {
          minWidth: 150,
        }
    return (
      <Container maxWidth='xl'>
        <Box flexGrow={1} padding='2rem'>
          <Box
            justifyContent='space-between'
            display='flex'
            flexDirection={isMobile ? 'column' : 'row'}
            gap={3}
            mb={3}
          >
            <Typography textAlign={isMobile ? 'center' : 'left'} variant='h2'>
              {t('user_plural')}
            </Typography>
            <Box
              display='flex'
              gap='1rem'
              flexDirection={isMobile ? 'row-reverse' : 'row'}
              justifyContent={isMobile ? 'space-between' : 'flex-end'}
            >
              <Button
                onClick={() => setOpenImportModal(true)}
                color='secondary'
                variant='text'
                sx={buttonSize}
              >
                <StyledIcon iconId='add' />
                {t('import')}
              </Button>
              <UserImport
                onAddUsers={(newUsers: User[]) =>
                  setUsers([...users, ...newUsers])
                }
                open={openImportModal}
                onClose={() => setOpenImportModal(false)}
              />
              <Button
                variant='contained'
                sx={buttonSize}
                onClick={() => {
                  setUser(
                    User.create({
                      email: '',
                      personal_number: '',
                      first_name: '',
                      last_name: '',
                      password: 'password',
                      title: '',
                      language: Language.SV,
                      user_type: UserType.Participant,
                    })
                  )
                  setOpenModal(true)
                }}
              >
                <StyledIcon iconId='add' />
                {t('add_users')}
              </Button>
            </Box>
          </Box>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={3}
            gap={2}
          >
            <Box>
              <Button
                color='secondary'
                variant='outlined'
                sx={buttonSize}
                disabled={selectedRowsIds.length !== 1}
                onClick={() => (window.location.href = `mailto:${email}`)}
              >
                {t('send_message')}
              </Button>
            </Box>
            <Box>
              <Typography variant='body1'>
                {t('count', { count: users.length })}
              </Typography>
            </Box>
          </Box>
          {isLoading ? (
            <Loading />
          ) : (
            <Box>
              {/* User list */}
              <DataGrid
                autoHeight
                checkboxSelection
                disableColumnSelector
                disableSelectionOnClick
                components={{
                  NoRowsOverlay: () => (
                    <DatagridPlaceholder
                      title={t('datagrid_placeholder_manager_participant_list')}
                    />
                  ),
                }}
                sx={{
                  borderRadius: 0,
                  backgroundColor: '#fff',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fff' },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-iconSeparator': { display: 'none' },
                  ...(hideFooter && {
                    '& .MuiDataGrid-footerContainer': { display: 'none' },
                  }),
                }}
                rowHeight={66}
                density='standard'
                rows={rowsArray}
                columns={columns}
                pageSize={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                hideFooterPagination={rowsArray.length < PAGE_SIZE}
                onSelectionModelChange={(ids: GridSelectionModel) =>
                  setSelectedRowsIds(ids as number[])
                }
              />
            </Box>
          )}
          {user && (
            <PopUpMenu
              anchorEl={anchorEl}
              TransitionComponent={Fade}
              open={Boolean(anchorEl && user)}
              onClose={() => {
                setAnchorEl(null)
                setOpenConfirmDialog(false)
              }}
            >
              <MenuItem
                onClick={() => {
                  window.location.href = `mailto:${user.email}`
                  setAnchorEl(null)
                }}
              >
                {t('send_message')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenConfirmDialog(true)
                }}
              >
                {t('delete')}
              </MenuItem>
              <ConfirmDialog
                content={
                  <Trans
                    t={t}
                    values={{ user: user?.email }}
                    i18nKey='delete_user_confirm_details'
                    components={{ bold: <strong /> }}
                  />
                }
                disabled={confirmValue !== user?.email}
                confirmInput={
                  <Input
                    value={confirmValue}
                    onChange={(e) => setConfirmValue(e.target.value)}
                  />
                }
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                title={t('confirm_delete_user_question') + '?'}
                confirmButtonText={t('delete')}
                cancelButtonText={t('cancel')}
                onCancel={() => {
                  setOpenConfirmDialog(false)
                  setAnchorEl(null)
                  setConfirmValue('')
                }}
                onConfirm={async () => {
                  if (await user.remove()) {
                    setUsers(users.filter((u) => u.id !== user.id))
                  } else {
                    toast(t('toast_error_remove_user'), 'error')
                  }
                  setAnchorEl(null)
                  setOpenConfirmDialog(false)
                }}
              />
            </PopUpMenu>
          )}
          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <Typography mb={3} variant='h3'>
              {t('add_users')}
            </Typography>
            <Grid
              container
              mb={4}
              rowSpacing={1}
              columnSpacing={theme.columnSpacing}
            >
              <UserForm
                required
                dontSave
                hasPersonalNumber
                hasConfirmationButtons
                user={user}
                onCancel={() => setOpenModal(false)}
                onSave={async () => {
                  if (await user.save()) {
                    setUsers([...users, user])
                    setOpenModal(false)
                  } else {
                    toast(t('toast_error_add_user_fail'), 'error')
                  }
                }}
              />
            </Grid>
          </Modal>
        </Box>
      </Container>
    )
  })
)

const StyledIcon = styled(Icon)`
  margin-right: 1rem;
`

const PopUpMenu = styled(Menu)`
  .MuiList-root {
    padding-top: 0;
    padding-bottom: 0;
  }

  .MuiMenuItem-root {
    :not(:last-child) {
      border-bottom: 2px solid #f4f4f0;
    }
    padding: 0.5rem 1rem;
  }
`
